import { Text, TextInput, TextInputProps, TextProps } from '@mantine/core';
import { useRef, useState, useEffect, ComponentType } from 'react';

export interface EditableFieldRenderInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<any>) => void;
  disabled: boolean;
  autoFocus: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  validationError?: string;
  inputProps?: TextInputProps;
}

export interface EditableFieldProps {
  value: string;
  displayValue?: string;
  placeholder?: string;
  onSubmit: (newValue: string) => Promise<void> | void;
  onChange?: (newValue: string) => void;
  onCancel?: () => void;
  onEditStart?: () => void;
  onEndEditing?: (info: { value: string; submitted: boolean; canceled: boolean }) => void;
  validate?: (value: string) => { valid: boolean; error?: string };
  liveValidate?: (value: string) => string | undefined; // Returns error message or undefined
  normalizeValue?: (value: string) => string;
  editable?: boolean; // default true
  textProps?: TextProps;
  inputProps?: TextInputProps;
  requireNonEmpty?: boolean;
  focus?: boolean;
  InputComponent?: ComponentType<EditableFieldRenderInputProps>;
}

export function EditableField({
  value,
  displayValue,
  placeholder,
  onSubmit,
  onChange,
  onCancel,
  onEditStart,
  onEndEditing,
  validate,
  liveValidate,
  normalizeValue,
  editable = true,
  textProps = {},
  inputProps = {},
  requireNonEmpty = false,
  focus = false,
  InputComponent,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [liveValidationError, setLiveValidationError] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const startTriggeredRef = useRef(false);
  const originalValueRef = useRef<string>(value);
  // Enter submits, then the input loses focus as it is disabled or closes: one submit, not two.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
      originalValueRef.current = value; // Keep original value in sync when not editing
    }
  }, [value, isEditing]);

  useEffect(() => {
    // Fire once when startEditing transitions to true
    if (focus && !startTriggeredRef.current) {
      startTriggeredRef.current = true;
      originalValueRef.current = value; // Capture original value
      setIsEditing(true);
      setEditValue(value);
      setTimeout(() => inputRef.current?.focus(), 0);
    } else if (!focus && startTriggeredRef.current) {
      // Allow future triggers when parent toggles startEditing back to false
      startTriggeredRef.current = false;
    }
  }, [focus, value]);

  // Live validation on edit value change
  useEffect(() => {
    if (isEditing && liveValidate) {
      const error = liveValidate(editValue);
      setLiveValidationError(error);
    }
  }, [editValue, isEditing, liveValidate]);

  const handleTextClick = () => {
    if (!editable) return;
    originalValueRef.current = value; // Capture original value
    setIsEditing(true);
    setEditValue(value);
    onEditStart?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputChange = (newValue: string) => {
    setEditValue(newValue);
    setValidationError(undefined); // Clear blocking validation error on change
    onChange?.(newValue);
  };

  const handleStandardInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e.target.value);
  };

  const trySubmit = async () => {
    if (submittingRef.current) return;
    const trimmed = editValue.trim();
    if (requireNonEmpty && !trimmed) {
      setEditValue(originalValueRef.current); // reset to original value
      setIsEditing(false);
      setValidationError(undefined);
      setLiveValidationError(undefined);
      onEndEditing?.({ value: trimmed, submitted: false, canceled: false });
      return;
    }

    // Validate if validator provided
    if (validate) {
      const validation = validate(trimmed);
      if (!validation.valid) {
        setValidationError(validation.error);
        return; // Don't submit if validation fails
      }
    }

    // Normalize value if normalizer provided
    const finalValue = normalizeValue ? normalizeValue(trimmed) : trimmed;

    // Only submit if user actually made a change (compare to original value when editing started)
    if (trimmed !== originalValueRef.current) {
      submittingRef.current = true;
      setIsSubmitting(true);
      try {
        await onSubmit(finalValue);
        setValidationError(undefined);
      } catch (_error) {
        setValidationError("Couldn't save");
        setIsSubmitting(false);
        return;
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    }
    setIsEditing(false);
    setValidationError(undefined);
    setLiveValidationError(undefined);
    onEndEditing?.({ value: finalValue, submitted: finalValue !== originalValueRef.current, canceled: false });
  };

  const handleInputBlur = () => {
    trySubmit();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      trySubmit();
    } else if (e.key === 'Escape') {
      const trimmed = editValue.trim();
      setEditValue(originalValueRef.current); // Restore to original value
      setIsEditing(false);
      setLiveValidationError(undefined);
      onCancel?.();
      onEndEditing?.({ value: trimmed, submitted: false, canceled: true });
    }
  };

  if (isEditing) {
    // Use custom InputComponent if provided
    if (InputComponent) {
      return (
        <InputComponent
          value={editValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={isSubmitting}
          autoFocus={true}
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
          validationError={liveValidationError || validationError}
          inputProps={inputProps}
        />
      );
    }

    // Default text input rendering
    return (
      <TextInput
        value={editValue}
        placeholder={placeholder}
        autoFocus
        onChange={handleStandardInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        ref={inputRef}
        {...inputProps}
        disabled={isSubmitting}
      />
    );
  }

  return (
    <Text
      style={{
        cursor: editable ? 'pointer' : undefined,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        minHeight: '10px',
        ...textProps.style,
      }}
      onClick={handleTextClick}
    >
      {(displayValue !== undefined ? displayValue : value) || placeholder || ''}
    </Text>
  );
}
